import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { GlobalEvents } from '@highlight/ipc'

import type { ChatHistoryItem } from '@renderer/chat'
import type { ForYouChatsCacheData } from '@renderer/overlay/hooks/conversation-preview-cache'
import type { SidepanelChatApiItem } from '@renderer/overlay/hooks/running-chat-selection'
import type { HistoryActivityInfiniteData } from '@renderer/overlay/Sidepanel/hooks/history-query-keys'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'
import {
  CHAT_HISTORY_QUERY_KEY,
  chatQueryKey,
  FOR_YOU_CHATS_QUERY_KEY,
  HISTORY_ACTIVITY_QUERY_KEY,
  HISTORY_PREVIEW_QUERY_KEY,
} from '@renderer/stores/conversation-query-keys'

/** Peer-window broadcast carrying a {@link ChatMetadataMap}. */
export const CHAT_METADATA_PATCH_EVENT = 'chat:metadata:patch'

export interface ChatMetadataEntry {
  id: string
  title?: string
  shares?: ChatHistoryItem['shares']
  updatedAt?: string
  createdAt?: string
  userId?: string
  isStreaming?: boolean
  type?: string
}

/** `null` is a tombstone so a delete rides the same patch shape as an upsert. */
export type ChatMetadataMap = Record<string, ChatMetadataEntry | null>

export interface ChatHistoryQueryPage {
  data: Array<ChatHistoryItem>
  nextCursor: string | undefined
}
export type ChatHistoryQueryData = InfiniteData<ChatHistoryQueryPage>

interface Row {
  id: string
}
interface PagedData {
  pages: Array<{ data: Array<Row> }>
}
type PageRows<TData extends PagedData> = TData['pages'][number]['data']
type RowMapper<TRow extends Row> = (rows: Array<TRow>) => Array<TRow>

/** Applies a row transform to every page of an infinite query, leaving page metadata alone. */
function mapPages<TData extends PagedData>(
  data: TData | undefined,
  map: (rows: PageRows<TData>, pageIndex: number) => PageRows<TData>,
): TData | undefined {
  if (!data?.pages) {
    return data
  }
  return {
    ...data,
    pages: data.pages.map((page, index) => ({ ...page, data: map(page.data as PageRows<TData>, index) })),
  }
}

/** The For You cache is a flat array for the unscoped feed and infinite data for scoped ones. */
function mapForYou(
  data: ForYouChatsCacheData | undefined,
  map: RowMapper<SidepanelChatApiItem>,
): ForYouChatsCacheData | undefined {
  if (!data) {
    return data
  }
  return Array.isArray(data) ? map(data) : mapPages(data, map)
}

function mergeChatFields<T extends Row>(item: T, entry: ChatMetadataEntry): T {
  const patch = Object.fromEntries(
    Object.entries(entry).filter(([key, value]) => key !== 'id' && value !== undefined),
  ) as Partial<T>
  return { ...item, ...patch }
}

function toChatHistoryItem(entry: ChatMetadataEntry): ChatHistoryItem {
  const now = new Date().toISOString()
  return {
    id: entry.id,
    userId: entry.userId ?? '',
    createdAt: entry.createdAt ?? now,
    updatedAt: entry.updatedAt ?? now,
    type: entry.type ?? 'chat',
    isStreaming: entry.isStreaming ?? false,
    title: entry.title,
    shares: entry.shares,
  }
}

function patchRow<T extends Row>(entry: ChatMetadataEntry): RowMapper<T> {
  return (rows) => rows.map((row) => (row.id === entry.id ? mergeChatFields(row, entry) : row))
}

function dropRow<T extends Row>(chatId: string): RowMapper<T> {
  return (rows) => rows.filter((row) => row.id !== chatId)
}

/** History activity mixes chats with audio notes; only chat rows are ours to touch. */
function isChatActivity(row: RecentActivity, chatId: string): boolean {
  return row.type === 'chat' && row.id === chatId
}

/**
 * RecentActivity is not a ChatHistoryItem subset: it has no updatedAt or
 * shares, and its sort key is the derived unifiedUpdatedAt. Map the patch
 * fields explicitly so a rename re-sorts the feed instead of leaving the row
 * parked in its old timeframe group with stray keys attached.
 */
function patchActivityRow(row: RecentActivity, entry: ChatMetadataEntry): RecentActivity {
  return {
    ...row,
    ...(entry.title !== undefined ? { title: entry.title } : {}),
    ...(entry.updatedAt !== undefined ? { unifiedUpdatedAt: new Date(entry.updatedAt) } : {}),
  }
}

/** Patches the row where it exists; otherwise prepends it so a brand-new chat shows up at once. */
function upsertRows(rows: Array<ChatHistoryItem>, entry: ChatMetadataEntry): Array<ChatHistoryItem> {
  return rows.some((row) => row.id === entry.id)
    ? patchRow<ChatHistoryItem>(entry)(rows)
    : [toChatHistoryItem(entry), ...rows]
}

function upsertChatHistoryPages(
  data: ChatHistoryQueryData | undefined,
  entry: ChatMetadataEntry,
): ChatHistoryQueryData | undefined {
  const found = data?.pages.some((page) => page.data.some((item) => item.id === entry.id)) ?? false
  return mapPages(data, (rows, pageIndex) =>
    pageIndex === 0 && !found ? [toChatHistoryItem(entry), ...rows] : patchRow<ChatHistoryItem>(entry)(rows),
  )
}

function applyEntry(queryClient: QueryClient, entry: ChatMetadataEntry): void {
  queryClient.setQueryData<ChatHistoryQueryData>([CHAT_HISTORY_QUERY_KEY], (data) =>
    upsertChatHistoryPages(data, entry),
  )
  queryClient.setQueryData<Array<ChatHistoryItem>>([HISTORY_PREVIEW_QUERY_KEY], (items) =>
    items ? upsertRows(items, entry) : items,
  )
  queryClient.setQueriesData<HistoryActivityInfiniteData>({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] }, (data) =>
    mapPages(data, (rows) => rows.map((row) => (isChatActivity(row, entry.id) ? patchActivityRow(row, entry) : row))),
  )
  queryClient.setQueriesData<ForYouChatsCacheData>({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }, (data) =>
    mapForYou(data, patchRow(entry)),
  )
  queryClient.setQueryData<ChatHistoryItem>(chatQueryKey(entry.id), (prev) =>
    prev ? mergeChatFields(prev, entry) : prev,
  )
}

function applyRemoval(queryClient: QueryClient, chatId: string): void {
  queryClient.setQueryData<ChatHistoryQueryData>([CHAT_HISTORY_QUERY_KEY], (data) => mapPages(data, dropRow(chatId)))
  queryClient.setQueryData<Array<ChatHistoryItem>>([HISTORY_PREVIEW_QUERY_KEY], (items) =>
    items ? dropRow<ChatHistoryItem>(chatId)(items) : items,
  )
  queryClient.setQueriesData<HistoryActivityInfiniteData>({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] }, (data) =>
    mapPages(data, (rows) => rows.filter((row) => !isChatActivity(row, chatId))),
  )
  queryClient.setQueriesData<ForYouChatsCacheData>({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] }, (data) =>
    mapForYou(data, dropRow(chatId)),
  )
  queryClient.removeQueries({ queryKey: chatQueryKey(chatId) })
}

/**
 * Patches every conversation-shaped React Query cache this renderer holds.
 * Runs for local writes and again when a peer window's patch arrives.
 */
export function applyChatMetadata(queryClient: QueryClient, metadata: ChatMetadataMap): void {
  for (const [chatId, entry] of Object.entries(metadata)) {
    if (entry === null) {
      applyRemoval(queryClient, chatId)
      continue
    }
    applyEntry(queryClient, entry)
  }
}

/**
 * Applies a patch here and forwards it to every other renderer window, which
 * apply it through {@link registerChatMetadataSync}'s listener. Only the
 * changed chats cross IPC; each window already holds the full caches.
 */
export function publishChatMetadata(queryClient: QueryClient, patch: ChatMetadataMap): void {
  applyChatMetadata(queryClient, patch)
  GlobalEvents.emitToPeers(CHAT_METADATA_PATCH_EVENT, patch)
}

/**
 * Subscribes this window's query cache to peer windows' metadata patches.
 * Registered at `ReactQueryProvider` because any window may publish, so any
 * window with a cache must listen. Returns an unsubscribe.
 */
export function registerChatMetadataSync(queryClient: QueryClient): () => void {
  const onPatch = (patch: ChatMetadataMap) => applyChatMetadata(queryClient, patch)
  GlobalEvents.on(CHAT_METADATA_PATCH_EVENT, onPatch)
  return () => {
    GlobalEvents.off(CHAT_METADATA_PATCH_EVENT, onPatch)
  }
}
